// Authored cells, bond connectivity and planar rigid clusters. There is no FEM
// stress solve, no fragment-to-fragment collision, and no implicit fracture mesh.
const STEP = 1 / 120;
const clamp = (n, low, high) => Math.max(low, Math.min(high, n));
const finite = (n, fallback) => Number.isFinite(n) ? n : fallback;
function configOf(c = {}) {
  return { structure:c.structure === 'bridge' ? 'bridge' : 'building', mode:c.mode === 'cosmetic' ? 'cosmetic' : 'structural',
    bondStrength:clamp(finite(c.bondStrength,.65),.05,2.5), impactRadius:clamp(finite(c.impactRadius,1.3),.3,3),
    bodyBudget:clamp(Math.round(finite(c.bodyBudget,6)),1,24), lifetime:clamp(finite(c.lifetime,8),1,30), sleeping:c.sleeping !== false };
}

function structureCells(structure) {
  const cells = [], grid = new Map(), bonds = [];
  const bridge = structure === 'bridge', columns = bridge ? 14 : 10, rows = bridge ? 7 : 9;
  for (let row=0;row<rows;row++) for(let column=0;column<columns;column++) {
    const pier = [1,2,11,12].includes(column);
    if (bridge && row < 4 && !pier) continue;
    if (!bridge && [2,3,5,6].includes(row) && [2,3,6,7].includes(column)) continue;
    const id=cells.length, x=(column-(columns-1)/2)*(bridge?.57:.65), y=.29+row*.59;
    const cell={id,x,y,w:bridge?.54:.62,h:.56,anchor:row===0,body:null,supported:true};cells.push(cell);grid.set(`${column},${row}`,id);
    for(const [x0,y0] of [[column-1,row],[column,row-1]])if(grid.has(`${x0},${y0}`))bonds.push({a:grid.get(`${x0},${y0}`),b:id,broken:false});
  }
  return {cells,bonds};
}

export class DestructionBudgetModel {
  constructor(config = {}) { this.reset(config); }
  reset(config = this.config) {
    this.config=configOf(config);const {cells,bonds}=structureCells(this.config.structure);
    this.cells=cells;this.bonds=bonds;this.bodies=[];this.time=0;this.tick=0;this.accumulator=0;this.impacts=[];this.nextBodyId=0;this.expiredCount=0;
    this.initialPieces=cells.length;return this.snapshot();
  }
  configure(config) {
    const next=configOf({...this.config,...config});
    if(next.structure!==this.config.structure||next.mode!==this.config.mode)return this.reset(next);
    this.config=next;if(!next.sleeping)for(const body of this.bodies)if(body.state==='sleeping')body.state='queued';
    this._budget();return this.snapshot();
  }
  removeSupports(side = 'both') {
    if(this.config.mode==='cosmetic')return this.snapshot();
    for(const cell of this.cells)if(cell.anchor&&(side==='both'||side==='left'&&cell.x<0||side==='right'&&cell.x>=0))cell.anchor=false;
    this._connectivity({x:0,y:0,strength:0});this._budget();return this.snapshot();
  }
  impact(x=0,y=3) {
    x=clamp(finite(x,0),-4,4);y=clamp(finite(y,3),.2,5.5);
    const hit={x,y,time:this.time};this.impacts.push(hit);if(this.impacts.length>8)this.impacts.shift();
    if(this.config.mode==='cosmetic') {
      for(let i=0;i<14;i++) {
        const a=i*2.399963, radius=.14+(i%5)*.13, size=.11+(i%3)*.035;
        this._body([{id:`shard-${this.nextBodyId}`,x:x+Math.cos(a)*radius,y:y+Math.sin(a)*radius,w:size,h:size}],{x,y,strength:1.6},true);
      }
    } else {
      for(const bond of this.bonds)if(!bond.broken) {
        const a=this.cells[bond.a],b=this.cells[bond.b];if(a.body!==null||b.body!==null)continue;
        const d=Math.hypot((a.x+b.x)/2-x,(a.y+b.y)/2-y);
        const damage=1.8*Math.exp(-d*d/(this.config.impactRadius*this.config.impactRadius));
        if(damage>this.config.bondStrength)bond.broken=true;
      }
      this._connectivity({x,y,strength:1.8});
    }
    this._budget();return this.snapshot();
  }
  _connectivity(impact) {
    const graph=this.cells.map(()=>[]),seen=new Set();
    for(const b of this.bonds)if(!b.broken&&this.cells[b.a].body===null&&this.cells[b.b].body===null){graph[b.a].push(b.b);graph[b.b].push(b.a);}
    for(const cell of this.cells) {
      if(cell.body!==null||seen.has(cell.id))continue;
      const ids=[cell.id];seen.add(cell.id);
      for(let i=0;i<ids.length;i++)for(const id of graph[ids[i]])if(!seen.has(id)){seen.add(id);ids.push(id);}
      const anchored=ids.some(id=>this.cells[id].anchor);
      for(const id of ids)this.cells[id].supported=anchored;
      if(!anchored)this._body(ids.map(id=>this.cells[id]),impact,false);
    }
  }
  _body(cells,impact,cosmetic) {
    const id=this.nextBodyId++,x=cells.reduce((s,p)=>s+p.x,0)/cells.length,y=cells.reduce((s,p)=>s+p.y,0)/cells.length;
    const distance=Math.hypot(x-impact.x,y-impact.y),kick=impact.strength*Math.exp(-distance*.3);
    const body={id,x,y,vx:(x-impact.x)*kick*.8,vy:kick*(.55+id%3*.2),angle:0,omega:(id%7-3)*kick*.7,
      state:'queued',born:this.time,rest:0,cosmetic,cells:cells.map(p=>({id:p.id,dx:p.x-x,dy:p.y-y,w:p.w,h:p.h}))};
    if(!cosmetic)for(const p of cells)p.body=id;
    this.bodies.push(body);
  }
  _budget() {
    const candidates=this.bodies.filter(b=>b.state==='active'||b.state==='queued').sort((a,b)=>b.cells.length-a.cells.length||a.id-b.id);
    candidates.forEach((body,index)=>{body.state=index<this.config.bodyBudget?'active':'queued';});
  }
  step(elapsed) {
    this.accumulator+=clamp(finite(elapsed,0),0,.25);
    while(this.accumulator+1e-10>=STEP) {
      this.accumulator-=STEP;this.tick++;this.time=this.tick*STEP;
      for(const body of this.bodies) {
        if(body.state==='expired')continue;
        if(this.time-body.born>=this.config.lifetime){body.state='expired';continue;}
        if(body.state!=='active')continue;
        body.vy-=9.81*STEP;body.x+=body.vx*STEP;body.y+=body.vy*STEP;body.angle+=body.omega*STEP;
        const co=Math.cos(body.angle),si=Math.sin(body.angle);
        let lowest=Infinity;
        for(const cell of body.cells)lowest=Math.min(lowest,body.y+cell.dx*si+cell.dy*co-Math.abs(si)*cell.w/2-Math.abs(co)*cell.h/2);
        if(lowest<0) {
          body.y-=lowest;body.vy=Math.abs(body.vy)<.3?0:Math.abs(body.vy)*.12;body.vx*=.89;body.omega*=.91;
          body.rest=Math.hypot(body.vx,body.vy)<.14&&Math.abs(body.omega)<.15?body.rest+STEP:0;
          if(this.config.sleeping&&body.rest>.45){body.state='sleeping';body.vx=body.vy=body.omega=0;}
        } else body.rest=0;
      }
      const expired=this.bodies.filter(body=>body.state==='expired').length;
      if(expired){this.expiredCount+=expired;this.bodies=this.bodies.filter(body=>body.state!=='expired');}
      this._budget();
    }
    return this.snapshot();
  }
  get stats() {
    return {pieces:this.initialPieces,supported:this.cells.filter(p=>p.supported).length,detached:this.cells.filter(p=>p.body!==null).length,
      bonds:this.bonds.length,broken:this.bonds.filter(b=>b.broken).length,supports:this.cells.filter(p=>p.anchor&&p.body===null).length,
      active:this.bodies.filter(b=>b.state==='active').length,queued:this.bodies.filter(b=>b.state==='queued').length,
      sleeping:this.bodies.filter(b=>b.state==='sleeping').length,expired:this.expiredCount};
  }
  snapshot() {
    const pieces=this.cells.filter(p=>p.body===null).map(p=>({...p,angle:0,state:'supported',opacity:1}));
    for(const body of this.bodies)if(body.state!=='expired') {
      const co=Math.cos(body.angle),si=Math.sin(body.angle),opacity=clamp((this.config.lifetime-(this.time-body.born))/.7,0,1);
      for(const cell of body.cells)pieces.push({id:cell.id,x:body.x+cell.dx*co-cell.dy*si,y:body.y+cell.dx*si+cell.dy*co,w:cell.w,h:cell.h,angle:body.angle,state:body.state,body:body.id,anchor:false,opacity,cosmetic:body.cosmetic});
    }
    return {time:this.time,tick:this.tick,config:{...this.config},stats:this.stats,pieces,bonds:this.bonds.map(b=>({...b})),impacts:this.impacts.map(p=>({...p}))};
  }
}
